import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  ShareIcon,
  XMarkIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import {
  SHARE_EXPIRY_OPTIONS,
  computeExpiresAt,
  formatShareExpiry,
} from '../../utils/shareExpiryOptions';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: ${theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: ${theme.borderRadius.sm};

  &:hover {
    color: ${theme.colors.text.primary};
    background: ${theme.colors.background.secondary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Subtitle = styled.p`
  margin: 0 0 1.25rem 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;
  min-height: 72px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary.main};
  color: white;
  font-weight: ${theme.fontWeights.semibold};
  cursor: pointer;
  margin-bottom: 1.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
`;

const ShareMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const ShareName = styled.div`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`;

const ShareEmail = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShareMetaLine = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  &:hover {
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary.main};
  }
`;

const RevokeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  color: ${theme.colors.error?.main || '#ef4444'};
  cursor: pointer;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const EmptyText = styled.p`
  margin: 0;
  padding: 1rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-style: italic;
  text-align: center;
`;

const ShareVesselTrackingModal = ({ isOpen, onClose, vessel }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [expiry, setExpiry] = useState('forever');

  const vesselId = vessel?.id;

  const loadShares = async () => {
    if (!vesselId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/vessel-tracking-sharing/${vesselId}/shares`);
      setShares(res.data?.shares || []);
    } catch (err) {
      console.error('Error loading vessel tracking shares:', err);
      toast.error(err.response?.data?.error || 'Failed to load shares');
      setShares([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && vesselId) {
      setEmail('');
      setName('');
      setMessage('');
      setExpiry('forever');
      loadShares();
    }
  }, [isOpen, vesselId]);

  const handleShare = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter an email address');
      return;
    }
    try {
      setSubmitting(true);
      const res = await api.post(`/api/vessel-tracking-sharing/${vesselId}/shares`, {
        recipient_email: trimmed,
        recipient_name: name.trim() || undefined,
        share_message: message.trim() || undefined,
        expires_at: computeExpiresAt(expiry),
      });
      toast.success(`Tracking link sent to ${trimmed}`);
      setEmail('');
      setName('');
      setMessage('');
      setExpiry('forever');
      await loadShares();
      if (res.data?.shareUrl) {
        toast((t) => (
          <div className="flex flex-col space-y-2">
            <span className="text-sm">Share link created. Copy if needed:</span>
            <button
              type="button"
              className="text-left text-sm text-blue-600 underline break-all"
              onClick={() => {
                navigator.clipboard.writeText(res.data.shareUrl);
                toast.success('Link copied');
                toast.dismiss(t.id);
              }}
            >
              {res.data.shareUrl}
            </button>
          </div>
        ), { duration: 8000 });
      }
    } catch (err) {
      console.error('Error sharing vessel tracking:', err);
      toast.error(err.response?.data?.error || 'Failed to send tracking link');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = (shareToken) => {
    const shareUrl = `${window.location.origin}/shared-vessel/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  const performRevoke = async (shareId, label) => {
    try {
      await api.delete(`/api/vessel-tracking-sharing/${vesselId}/shares/${shareId}`);
      toast.success(`Revoked link for ${label}`);
      await loadShares();
    } catch (err) {
      console.error('Error revoking vessel tracking share:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke link');
    }
  };

  const handleRevoke = (shareId, label) => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Revoke tracking link</span>
        <span className="text-sm text-gray-600">
          Revoke the link for <strong>{label}</strong>? It will stop working immediately.
        </span>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              performRevoke(shareId, label);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Revoke link
          </button>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
  };

  if (!isOpen || !vessel) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <LinkIcon className="w-5 h-5" />
            Share live tracking
          </Title>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <Subtitle>
            Email a secure map link for <strong>{vessel.vessel_name}</strong>.
            Recipients can follow live position without a BoatWise account.
          </Subtitle>

          <Label htmlFor="vessel-share-email">Email address</Label>
          <TextInput
            id="vessel-share-email"
            type="email"
            placeholder="friend@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />

          <Label htmlFor="vessel-share-name">Name (optional)</Label>
          <TextInput
            id="vessel-share-name"
            type="text"
            placeholder="Recipient name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />

          <Label htmlFor="vessel-share-expiry">Link expires</Label>
          <Select
            id="vessel-share-expiry"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          >
            {SHARE_EXPIRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>

          <Label htmlFor="vessel-share-message">Personal message (optional)</Label>
          <TextArea
            id="vessel-share-message"
            placeholder="Add a note to include in the email…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <PrimaryButton
            type="button"
            onClick={handleShare}
            disabled={submitting || !email.trim()}
          >
            {submitting ? 'Sending…' : 'Send tracking link'}
          </PrimaryButton>

          <Label>Active tracking links</Label>
          {loading ? (
            <EmptyText>Loading…</EmptyText>
          ) : shares.filter((s) => s.is_active !== false).length === 0 ? (
            <EmptyText>No external tracking links yet.</EmptyText>
          ) : (
            <ShareList>
              {shares.filter((s) => s.is_active !== false).map((row) => (
                <ShareRow key={row.id}>
                  <ShareMeta>
                    <ShareName>{row.recipient_name || row.recipient_email}</ShareName>
                    <ShareEmail>{row.recipient_email}</ShareEmail>
                    <ShareMetaLine>
                      {formatShareExpiry(row.expires_at)}
                      {row.access_count > 0 && ` · ${row.access_count} view${row.access_count === 1 ? '' : 's'}`}
                    </ShareMetaLine>
                  </ShareMeta>
                  <SecondaryButton type="button" onClick={() => copyLink(row.share_token)}>
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copy
                  </SecondaryButton>
                  <RevokeButton
                    type="button"
                    title="Revoke link"
                    onClick={() => handleRevoke(row.id, row.recipient_email)}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </RevokeButton>
                </ShareRow>
              ))}
            </ShareList>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default ShareVesselTrackingModal;
